import type { CalendarProps } from "@fluentui/react-calendar-compat";
import { Calendar, DateRangeType } from "@fluentui/react-calendar-compat";
import moment from "moment";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FluentProvider } from "@/dependencies";
import { CalendarBaseWrapper } from "./Style";

interface CalendarCustomProps {
  rcName?: string;
  theme?: string;
  width?: number;
  isMultiple?: boolean;
  onChange?: (date: Date, dateRange: Date[]) => void;
  selectedDate?: Date | Date[];
}

const CalendarCustom = (props: CalendarCustomProps & CalendarProps) => {
  const [daysToSelectInDayView, setDaysToSelectInDayView] = useState(0);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [selectedDateRange, setSelectedDateRange] = useState<Date[]>([]);

  useEffect(() => {
    if (
      props.isMultiple &&
      props.selectedDate &&
      Array.isArray(props.selectedDate) &&
      props.selectedDate.length === 2
    ) {
      setSelectedDateRange(props.selectedDate);
      setSelectedDate(props.selectedDate[0]);
      const start = moment(props.selectedDate[0]);
      const end = moment(props.selectedDate[1]);
      const between = end.diff(start, "days");
      setDaysToSelectInDayView(between + 1);
    } else if (
      !props.isMultiple &&
      props.selectedDate &&
      Array.isArray(props.selectedDate) &&
      props.selectedDate.length
    ) {
      setSelectedDate(props.selectedDate[0] as Date);
    } else if (props.selectedDate) {
      setSelectedDate(props.selectedDate as Date);
    } else {
      setSelectedDate(new Date());
    }
  }, [props.isMultiple, props.selectedDate]);

  const onSelectDate = (date: Date): void => {
    if (props.isMultiple) {
      if (!selectedDateRange?.length || selectedDateRange.length === 2) {
        setSelectedDate(date);
        setSelectedDateRange([date]);
        if (daysToSelectInDayView) {
          setDaysToSelectInDayView(0);
        }
        if (props.onChange) {
          props.onChange(date, []);
        }
      } else if (selectedDateRange?.length === 1) {
        if (date.getTime() > selectedDateRange[0].getTime()) {
          const range = [selectedDateRange[0], date];
          setSelectedDate(selectedDateRange[0]);
          setSelectedDateRange(range);
          const start = moment(selectedDateRange[0]);
          const end = moment(date);
          const between = end.diff(start, "days");
          setDaysToSelectInDayView(between + 1);
          if (props.onChange) {
            props.onChange(date, range);
          }
        } else {
          const range = [date, selectedDateRange[0]];
          setSelectedDateRange(range);
          setSelectedDate(date);
          const start = moment(date);
          const end = moment(selectedDateRange[0]);
          const between = end.diff(start, "days");
          setDaysToSelectInDayView(between + 1);
          if (props.onChange) {
            props.onChange(date, range);
          }
        }
      }
    } else {
      setSelectedDate(date);
      if (props.onChange) {
        props.onChange(date, []);
      }
    }
  };

  const { t } = useTranslation();
  const dayPickerStrings = {
    months: [
      t("January"),
      t("February"),
      t("March"),
      t("April"),
      t("May"),
      t("June"),
      t("July"),
      t("August"),
      t("September"),
      t("October"),
      t("November"),
      t("December")
    ],
    shortMonths: [
      t("Jan"),
      t("Feb"),
      t("Mar"),
      t("Apr"),
      t("May"),
      t("Jun"),
      t("Jul"),
      t("Aug"),
      t("Sep"),
      t("Oct"),
      t("Nov"),
      t("Dec")
    ],
    days: [
      t("Sunday"),
      t("Monday"),
      t("Tuesday"),
      t("Wednesday"),
      t("Thursday"),
      t("Friday"),
      t("Saturday")
    ],
    shortDays: [t("S"), t("M"), t("T"), t("W"), t("T"), t("F"), t("S")],
    goToToday: t("Today"),
    prevMonthAriaLabel: t("Previous month"),
    nextMonthAriaLabel: t("Next month"),
    prevYearAriaLabel: t("Previous year"),
    nextYearAriaLabel: t("Next year"),
    closeButtonAriaLabel: t("Close"),
    isRequiredErrorMessage: t("Field is required."),
    invalidInputErrorMessage: t("Invalid date format.")
  };

  const calendarStrings = props.strings || dayPickerStrings;

  return props.theme ? (
    <FluentProvider theme={props.theme}>
      <CalendarBaseWrapper className='CalendarBaseWrapper' $width={props.width}>
        <Calendar
          {...props}
          strings={calendarStrings}
          onSelectDate={onSelectDate}
          value={selectedDate}
          calendarDayProps={props.isMultiple ? { daysToSelectInDayView } : undefined}
          dateRangeType={props.dateRangeType || DateRangeType.Day}
        />
      </CalendarBaseWrapper>
    </FluentProvider>
  ) : (
    <CalendarBaseWrapper className='CalendarBaseWrapper' $width={props.width}>
      <Calendar
        {...props}
        strings={calendarStrings}
        onSelectDate={onSelectDate}
        value={selectedDate}
        calendarDayProps={props.isMultiple ? { daysToSelectInDayView } : undefined}
        dateRangeType={props.dateRangeType || DateRangeType.Day}
      />
    </CalendarBaseWrapper>
  );
};

export default CalendarCustom;
